import { getCookieValue, getCookieValueFromHeader, getDocumentCookieHeader, setCookieValue } from "@/services/cookies/cookies.service";

export const ANALYTICS_COOKIE_NAME = "analytics-cookies";
export const CLARITY_COOKIE_NAME = "clarity-cookies";
export const CONSENT_COOKIE_NAME = "cookieConsent";

export type CookieConsent = "all" | "custom" | "essential";

export interface CookiePreferences {
  analytics: boolean;
  clarity: boolean;
}

const CONSENT_DECISIONS = new Set<CookieConsent>(["all", "custom", "essential"]);
const DEFAULT_CONSENT_PREFERENCES: CookiePreferences = {
  analytics: false,
  clarity: false,
};

function getStoredConsentPreferences(): CookiePreferences | null {
  const cookieHeader = getDocumentCookieHeader();
  const analyticsCookie = getCookieValueFromHeader(cookieHeader, ANALYTICS_COOKIE_NAME);
  const clarityCookie = getCookieValueFromHeader(cookieHeader, CLARITY_COOKIE_NAME);

  if (analyticsCookie === null && clarityCookie === null) {
    return null;
  }

  return { analytics: analyticsCookie === "true", clarity: clarityCookie === "true" };
}

function getPreferencesForConsent(consent: CookieConsent): CookiePreferences {
  if (consent === "all") {
    return { analytics: true, clarity: true };
  }

  return { ...DEFAULT_CONSENT_PREFERENCES };
}

function parseConsentDecision(value: string | null): CookieConsent | null {
  if (!value || !CONSENT_DECISIONS.has(value as CookieConsent)) {
    return null;
  }

  return value as CookieConsent;
}

export function getConsentDecision(): CookieConsent | null {
  return parseConsentDecision(getCookieValue(CONSENT_COOKIE_NAME));
}

export function getConsentPreferences(): CookiePreferences {
  return getStoredConsentPreferences() ?? { ...DEFAULT_CONSENT_PREFERENCES };
}

export function hasConsentDecision(): boolean {
  return getConsentDecision() !== null || getStoredConsentPreferences() !== null;
}

export function saveConsentDecision(consent: CookieConsent, preferences = getPreferencesForConsent(consent)): void {
  setCookieValue(ANALYTICS_COOKIE_NAME, String(preferences.analytics));
  setCookieValue(CLARITY_COOKIE_NAME, String(preferences.clarity));
  setCookieValue(CONSENT_COOKIE_NAME, consent);
}
